import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { 
  ArrowRight, CheckCircle, Star, Clock, Users, 
  TrendingUp, Shield, Zap, Heart
} from 'lucide-react';

const AboutPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const content = {
    nl: {
      // Hero
      badge: 'Nu live',
      heroTitle: 'Wij zochten 8 maanden.',
      heroTitleHighlight: 'Niemand reageerde.',
      heroSubtitle: 'Daarom bouwden we Swipe je woning. Een platform waar je wél binnen 2 weken een woning vindt.',
      heroCta: 'Probeer het gratis',
      heroSubCta: 'Geen creditcard nodig',
      
      // Stats
      stat1: '1.247',
      stat1Label: 'actieve zoekers',
      stat2: '14 dagen',
      stat2Label: 'gemiddeld tot match',
      stat3: '82%',
      stat3Label: 'vindt woning',
      
      // Problem section
      problemLabel: 'Het probleem',
      problemTitle: 'Woning zoeken in Nederland is een ramp.',
      problemPoints: [
        '200+ berichten sturen, 0 reacties krijgen',
        'Dezelfde documenten 10x uploaden',
        'Uren scrollen door nep advertenties',
        'Bezichtigingen die nergens toe leiden'
      ],
      
      // Solution section
      solutionLabel: 'De oplossing',
      solutionTitle: 'Swipe, match, verhuizen.',
      solutionDesc: 'Geen eindeloos scrollen. Geen copy-paste berichten. Gewoon swipen door woningen die bij je passen en direct matchen met verhuurders.',
      solutionFeatures: [
        { title: 'Snel', desc: 'In 2 weken een woning' },
        { title: 'Veilig', desc: 'Elke woning gecheckt' },
        { title: 'Gratis', desc: '3 swipes per dag' }
      ],
      
      // Story section
      storyLabel: 'Ons verhaal',
      storyTitle: 'Gemaakt door mensen die het zelf meemaakten.',
      storyText: 'Wij zijn Tom, Lisa en Mark. Allemaal weten we hoe frustrerend het is om een woning te zoeken in Nederland. Tom stuurde 200+ berichten zonder reactie. Lisa moest 3x verhuizen door slechte verhuurders. Mark kon als expat helemaal niks vinden.',
      storyHighlight: 'Nu helpen we anderen met hetzelfde probleem.',
      
      // Team
      team: [
        { name: 'Tom', role: 'Oprichter', story: '8 maanden gezocht, 0 reacties' },
        { name: 'Lisa', role: 'Tech', story: '3x verhuisd in 2 jaar' },
        { name: 'Mark', role: 'Community', story: 'Als expat was het onmogelijk' }
      ],
      
      // Social proof
      reviewsTitle: 'Wat gebruikers zeggen',
      reviews: [
        { text: 'Binnen 2 weken een studio gevonden in Amsterdam!', author: 'Fleur, 23', city: 'Amsterdam' },
        { text: 'Eindelijk geen 100 berichten meer sturen.', author: 'Daan, 27', city: 'Rotterdam' },
        { text: 'Simpel en het werkt gewoon.', author: 'Emma, 25', city: 'Utrecht' }
      ],
      
      // CTA
      ctaTitle: 'Klaar om te starten?',
      ctaDesc: 'Onbeperkt swipen. Geen gedoe.',
      ctaButton: 'Start nu gratis',
      ctaPoints: ['Gratis account', 'Direct beginnen', 'Geen spam']
    },
    en: {
      // Hero
      badge: 'Now live',
      heroTitle: 'We searched for 8 months.',
      heroTitleHighlight: 'Nobody responded.',
      heroSubtitle: "That's why we built Swipe je woning. A platform where you actually find a home within 2 weeks.",
      heroCta: 'Try it free',
      heroSubCta: 'No credit card needed',
      
      // Stats
      stat1: '1,247',
      stat1Label: 'active searchers',
      stat2: '14 days',
      stat2Label: 'average to match',
      stat3: '82%',
      stat3Label: 'find a home',
      
      // Problem section
      problemLabel: 'The problem',
      problemTitle: 'House hunting in the Netherlands is a nightmare.',
      problemPoints: [
        'Send 200+ messages, get 0 responses',
        'Upload the same documents 10x',
        'Hours scrolling through fake listings',
        'Viewings that lead nowhere'
      ],
      
      // Solution section
      solutionLabel: 'The solution',
      solutionTitle: 'Swipe, match, move.',
      solutionDesc: 'No endless scrolling. No copy-paste messages. Just swipe through homes that fit you and match directly with landlords.',
      solutionFeatures: [
        { title: 'Fast', desc: 'Home in 2 weeks' },
        { title: 'Safe', desc: 'Every home verified' },
        { title: 'Free', desc: '3 swipes per day' }
      ],
      
      // Story section
      storyLabel: 'Our story',
      storyTitle: 'Built by people who experienced it themselves.',
      storyText: "We are Tom, Lisa and Mark. We all know how frustrating it is to search for a home in the Netherlands. Tom sent 200+ messages without response. Lisa had to move 3 times due to bad landlords. Mark couldn't find anything as an expat.",
      storyHighlight: 'Now we help others with the same problem.',
      
      // Team
      team: [
        { name: 'Tom', role: 'Founder', story: 'Searched 8 months, 0 responses' },
        { name: 'Lisa', role: 'Tech', story: 'Moved 3x in 2 years' },
        { name: 'Mark', role: 'Community', story: 'As an expat it was impossible' }
      ],
      
      // Social proof
      reviewsTitle: 'What users say',
      reviews: [
        { text: 'Found a studio in Amsterdam within 2 weeks!', author: 'Fleur, 23', city: 'Amsterdam' },
        { text: "Finally no more sending 100 messages.", author: 'Daan, 27', city: 'Rotterdam' },
        { text: 'Simple and it just works.', author: 'Emma, 25', city: 'Utrecht' }
      ],
      
      // CTA
      ctaTitle: 'Ready to start?',
      ctaDesc: 'Unlimited swipes. No hassle.',
      ctaButton: 'Start free now',
      ctaPoints: ['Free account', 'Start immediately', 'No spam']
    }
  };

  const c = content[language];

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="about-page">
      <Header />
      
      <main className="flex-1 pt-16">
        {/* Hero Section - Clean & Impactful */}
        <section className="relative bg-[#FAFAFA] overflow-hidden">
          <div className="max-w-6xl mx-auto px-4 py-16 md:py-24">
            <div className="max-w-3xl">
              {/* Live badge */}
              <div className="inline-flex items-center gap-2 bg-[#2E7D32]/10 text-[#2E7D32] px-3 py-1.5 rounded-full text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-[#2E7D32] rounded-full animate-pulse" />
                {c.badge}
              </div>

              {/* Main headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#1A1A1A] leading-[1.1] mb-6">
                {c.heroTitle}
                <br />
                <span className="text-[#2E7D32]">{c.heroTitleHighlight}</span>
              </h1>

              <p className="text-lg sm:text-xl text-[#555] leading-relaxed mb-8 max-w-xl">
                {c.heroSubtitle}
              </p>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Button 
                  onClick={() => navigate('/swipematch')}
                  className="bg-[#2E7D32] hover:bg-[#256B2B] text-white h-12 px-8 text-base font-semibold rounded-lg"
                  data-testid="hero-cta-btn"
                >
                  {c.heroCta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <span className="text-sm text-[#888]">{c.heroSubCta}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="border-y border-[#E8E8E8] bg-white">
          <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl sm:text-3xl font-black text-[#2E7D32]">{c.stat1}</div>
                <div className="text-xs sm:text-sm text-[#888]">{c.stat1Label}</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-black text-[#2E7D32]">{c.stat2}</div>
                <div className="text-xs sm:text-sm text-[#888]">{c.stat2Label}</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-black text-[#2E7D32]">{c.stat3}</div>
                <div className="text-xs sm:text-sm text-[#888]">{c.stat3Label}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-16 md:py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="text-sm font-semibold text-[#2E7D32] uppercase tracking-wide mb-3">
                  {c.problemLabel}
                </div>
                <h2 className="text-3xl sm:text-4xl font-black text-[#1A1A1A] mb-6">
                  {c.problemTitle}
                </h2>
                <ul className="space-y-3">
                  {c.problemPoints.map((point, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#555]">
                      <span className="text-red-500 mt-1">✗</span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&q=80"
                  alt="Housing"
                  className="rounded-xl w-full shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-16 md:py-20 px-4 bg-[#FAFAFA]">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-2xl mx-auto text-center mb-12">
              <div className="text-sm font-semibold text-[#2E7D32] uppercase tracking-wide mb-3">
                {c.solutionLabel}
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-[#1A1A1A] mb-4">
                {c.solutionTitle}
              </h2>
              <p className="text-lg text-[#555]">{c.solutionDesc}</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {c.solutionFeatures.map((feature, i) => (
                <div key={i} className="bg-white rounded-xl p-6 border border-[#E8E8E8]">
                  <div className="w-10 h-10 bg-[#2E7D32]/10 rounded-lg flex items-center justify-center mb-4">
                    {i === 0 && <Zap className="w-5 h-5 text-[#2E7D32]" />}
                    {i === 1 && <Shield className="w-5 h-5 text-[#2E7D32]" />}
                    {i === 2 && <Heart className="w-5 h-5 text-[#2E7D32]" />}
                  </div>
                  <h3 className="text-xl font-bold text-[#1A1A1A] mb-1">{feature.title}</h3>
                  <p className="text-[#666]">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Story Section - Clean, no photos */}
        <section className="py-16 md:py-20 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="text-sm font-semibold text-[#2E7D32] uppercase tracking-wide mb-3">
                {c.storyLabel}
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-[#1A1A1A] mb-6">
                {c.storyTitle}
              </h2>
            </div>
            
            {/* Story timeline */}
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-[#E8E8E8] hidden md:block" />
              
              <div className="space-y-8">
                {/* Story point 1 */}
                <div className="flex gap-6 items-start">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#2E7D32] to-[#1F3A5F] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <span className="text-2xl font-black text-white">8</span>
                  </div>
                  <div className="pt-2">
                    <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">
                      {language === 'nl' ? '8 maanden zoeken, 0 reacties' : '8 months searching, 0 responses'}
                    </h3>
                    <p className="text-[#555] leading-relaxed">
                      {language === 'nl' 
                        ? 'We stuurden honderden berichten naar verhuurders. Niemand reageerde. We uploadden dezelfde documenten keer op keer. Frustrerend.'
                        : 'We sent hundreds of messages to landlords. No one responded. We uploaded the same documents over and over. Frustrating.'}
                    </p>
                  </div>
                </div>
                
                {/* Story point 2 */}
                <div className="flex gap-6 items-start">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#FF6B6B] to-[#EE5A5A] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <span className="text-2xl">💡</span>
                  </div>
                  <div className="pt-2">
                    <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">
                      {language === 'nl' ? 'Er moet een betere manier zijn' : 'There has to be a better way'}
                    </h3>
                    <p className="text-[#555] leading-relaxed">
                      {language === 'nl' 
                        ? 'Dating apps werken met swipen. Waarom woningen niet? Eén swipe, één match, één gesprek. Simpel.'
                        : 'Dating apps work with swiping. Why not housing? One swipe, one match, one conversation. Simple.'}
                    </p>
                  </div>
                </div>
                
                {/* Story point 3 */}
                <div className="flex gap-6 items-start">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#2E7D32] to-[#4CAF50] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <span className="text-2xl">🚀</span>
                  </div>
                  <div className="pt-2">
                    <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">
                      {language === 'nl' ? 'Swipe je woning is geboren' : 'Swipe je woning was born'}
                    </h3>
                    <p className="text-[#555] leading-relaxed">
                      {language === 'nl' 
                        ? 'Nu helpen we duizenden mensen hun droomwoning te vinden. Snel, simpel, zonder gedoe.'
                        : 'Now we help thousands of people find their dream home. Fast, simple, no hassle.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Mission statement */}
            <div className="mt-12 p-8 bg-gradient-to-br from-[#F8FFF8] to-[#F0F9F0] rounded-2xl border border-[#2E7D32]/20">
              <p className="text-xl text-center font-medium text-[#1A1A1A]">
                <span className="text-[#2E7D32]">"</span>
                {language === 'nl' 
                  ? 'Onze missie: iedereen in Nederland helpen om binnen 2 weken een woning te vinden.'
                  : 'Our mission: help everyone in the Netherlands find a home within 2 weeks.'}
                <span className="text-[#2E7D32]">"</span>
              </p>
              <p className="text-center text-[#666] mt-3">
                — {language === 'nl' ? 'Team Swipe je woning' : 'Team Swipe je woning'}
              </p>
            </div>
          </div>
        </section>

        {/* Reviews Section */}
        <section className="py-16 px-4 bg-[#FAFAFA] border-y border-[#E8E8E8]">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-center text-[#1A1A1A] mb-10">
              {c.reviewsTitle}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {c.reviews.map((review, i) => (
                <div key={i} className="bg-white rounded-xl p-6 border border-[#E8E8E8]">
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-[#333] mb-4">"{review.text}"</p>
                  <div className="text-sm text-[#888]">
                    — {review.author}, {review.city}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 md:py-20 px-4 bg-[#2E7D32]">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              {c.ctaTitle}
            </h2>
            <p className="text-lg text-white/80 mb-8">
              {c.ctaDesc}
            </p>
            
            <Button 
              onClick={() => navigate('/swipematch')}
              className="bg-white text-[#2E7D32] hover:bg-gray-100 h-12 px-10 text-base font-semibold rounded-lg"
              data-testid="final-cta-btn"
            >
              {c.ctaButton}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-white/70 text-sm">
              {c.ctaPoints.map((point, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>{point}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
